/**
 * Hook for polling binder processing status with auto-retry.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { binderAPI, roleAPI } from '../api/client';

const MAX_AUTO_RETRIES = 3;

interface UseProcessingPollerOptions {
  roleId: string | undefined;
  onComplete: () => void;
}

export function useProcessingPoller({ roleId, onComplete }: UseProcessingPollerOptions) {
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<any>(null);
  const [retrying, setRetrying] = useState(false);
  const [failedTaskId, setFailedTaskId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoRetryCountRef = useRef(0);

  const triggerAutoRetry = useCallback(async (taskId: string) => {
    if (autoRetryCountRef.current >= MAX_AUTO_RETRIES) {
      console.warn(`Max auto-retries (${MAX_AUTO_RETRIES}) reached, showing manual retry button`);
      setProcessing(false);
      setFailedTaskId(taskId);
      setError('Processing failed after multiple automatic retries. Click retry to try again.');
      return;
    }

    autoRetryCountRef.current += 1;
    console.log(`Auto-retry #${autoRetryCountRef.current} for task ${taskId}`);
    setError(null);
    setProcessing(true);

    try {
      await binderAPI.retryFailedTask(taskId);
      startPollingRef.current(taskId);
    } catch (err: any) {
      console.error('Auto-retry failed:', err);
      setProcessing(false);
      setFailedTaskId(taskId);
      setError(err.response?.data?.detail || 'Auto-retry failed. Click retry to try again.');
    }
  }, []);

  const startPollingRef = useRef<(taskId: string) => void>(() => {});

  const startPolling = useCallback((taskId: string) => {
    const checkStatus = async () => {
      try {
        const status = await binderAPI.getProcessingStatus(taskId);
        setProcessingStatus(status.data);

        if (status.data.is_stuck && status.data.status !== 'failed') {
          console.warn('Task is stuck — auto-retrying');
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          triggerAutoRetry(taskId);
        } else if (status.data.is_incomplete && status.data.status !== 'failed') {
          console.warn(`Incomplete: ${status.data.evaluations_in_db}/${status.data.resumes_detected} — auto-retrying`);
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          triggerAutoRetry(taskId);
        } else if (status.data.status === 'completed') {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          setProcessing(false);
          setTimeout(onComplete, 1000);
        } else if (status.data.status === 'failed') {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          triggerAutoRetry(taskId);
        }
      } catch (err: any) {
        console.error('Polling error:', err);
      }
    };

    checkStatus();
    pollIntervalRef.current = setInterval(checkStatus, 60000);
  }, [onComplete, triggerAutoRetry]);

  startPollingRef.current = startPolling;

  const checkActiveTask = useCallback(async () => {
    if (!roleId) return;
    try {
      const response = await roleAPI.getActiveTask(roleId);
      const { task_id, is_active } = response.data;
      if (is_active) {
        console.log(`Reconnecting to active task ${task_id}`);
        setProcessing(true);
        startPolling(task_id);
      } else {
        console.log(`Found non-active task ${task_id}, auto-retrying`);
        triggerAutoRetry(task_id);
      }
    } catch {
      // No active task — normal state
    }
  }, [roleId, startPolling, triggerAutoRetry]);

  const handleRetry = useCallback(async () => {
    if (!failedTaskId) return;

    setRetrying(true);
    setError(null);
    autoRetryCountRef.current = 0;

    try {
      await binderAPI.retryFailedTask(failedTaskId);
      setProcessing(true);
      setRetrying(false);
      setFailedTaskId(null);
      startPolling(failedTaskId);
    } catch (err: any) {
      console.error('Retry failed:', err);
      setError(err.response?.data?.detail || 'Retry failed. Please try again.');
      setRetrying(false);
    }
  }, [failedTaskId, startPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  return {
    processing,
    setProcessing,
    processingStatus,
    retrying,
    failedTaskId,
    error,
    setError,
    autoRetryCount: autoRetryCountRef.current,
    maxAutoRetries: MAX_AUTO_RETRIES,
    startPolling,
    checkActiveTask,
    handleRetry,
  };
}
